import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Navigation, CalendarDays } from "lucide-react";
import { useFetch, fmtDate, usePageTitle } from "@/lib/api";
import { IMG, directionsUrl } from "@/lib/content";
import { PageHero, Loading, EmptyState, Gallery, VideoEmbed } from "@/components/site/Bits";

const Block = ({ title, children, testid }) => (
  <div data-testid={testid}>
    <p className="eyebrow mb-5">{title}</p>
    {children}
  </div>
);
const None = ({ children }) => <p className="text-sm text-slate-400">{children}</p>;

export default function EventDetail() {
  const { slug } = useParams();
  const { data: e, loading, error } = useFetch(`/content/events/${slug}`);
  usePageTitle(e?.title || "Event");
  if (loading) return <div className="wrap pt-40"><Loading /></div>;
  if (error || !e) return <div className="wrap pt-40 pb-20"><EmptyState title="Event not found" testid="event-not-found" /></div>;
  return (
    <div data-testid="event-detail-page">
      <PageHero testid="event-hero" eyebrow={e.category || "Event"} image={IMG.night} title={e.title} sub={e.description}>
        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          <span className="flex items-center gap-2 bg-pitch/70 border border-line px-4 py-2"><CalendarDays className="h-4 w-4 text-gold" />{fmtDate(e.date, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
          {e.time_info && <span className="flex items-center gap-2 bg-pitch/70 border border-line px-4 py-2"><Clock className="h-4 w-4 text-gold" />{e.time_info}</span>}
          <span className="flex items-center gap-2 bg-pitch/70 border border-line px-4 py-2"><MapPin className="h-4 w-4 text-gold" />{e.location}{e.address ? ` • ${e.address}` : ""}</span>
        </div>
        <div className="mt-8 flex flex-wrap gap-4">
          {e.address && <a href={directionsUrl(e.address)} target="_blank" rel="noreferrer" className="btn-gold" data-testid="event-directions-button"><Navigation className="h-4 w-4" /> Directions</a>}
          <Link to="/events" className="btn-line" data-testid="event-back-link"><ArrowLeft className="h-4 w-4" /> All Events</Link>
        </div>
      </PageHero>
      <section className="bg-pitch py-16 sm:py-20">
        <div className="wrap space-y-14">
          <Block title="Participating Teams" testid="event-teams">
            {(e.participating_teams || []).length ? (
              <div className="grid gap-px bg-line/60 sm:grid-cols-2 lg:grid-cols-4">
                {e.participating_teams.map((t) => <div key={t} className={`p-6 font-display text-2xl font-black uppercase italic ${t.startsWith("Tri County") ? "bg-gold text-navy" : "bg-ink"}`}>{t}</div>)}
              </div>
            ) : <None>Participating teams will be announced.</None>}
          </Block>
          <div className="grid gap-12 lg:grid-cols-2">
            <Block title="Game Schedule" testid="event-schedule">
              {(e.schedule || []).length ? (
                <div className="space-y-px bg-line/60">{e.schedule.map((g, i) => <div key={i} className="bg-ink p-4 grid grid-cols-[90px_1fr] gap-3"><span className="font-mono text-sm text-gold">{g.time}</span><span><strong className="font-display text-lg uppercase italic">{g.matchup}</strong>{g.field && <span className="block text-xs text-slate-400">{g.field}</span>}</span></div>)}</div>
              ) : <None>Game schedule to be announced.</None>}
            </Block>
            <Block title="Scores" testid="event-scores">
              {(e.scores || []).length ? (
                <div className="space-y-px bg-line/60">
                  {e.scores.map((s, i) => (
                    <div key={i} className="bg-ink p-4">
                      {s.division && <p className="font-mono text-[10px] uppercase tracking-widest text-gold mb-2">{s.division}</p>}
                      <div className="flex justify-between font-display text-xl font-extrabold uppercase"><span>{s.home}</span><span className="text-gold">{s.home_score}</span></div>
                      <div className="flex justify-between font-display text-xl font-extrabold uppercase"><span>{s.away}</span><span className="text-gold">{s.away_score}</span></div>
                    </div>
                  ))}
                </div>
              ) : <None>Scores will be posted as games are completed.</None>}
            </Block>
          </div>
          <Block title="Tournament Results" testid="event-results">
            {e.results ? <p className="card-navy p-6 whitespace-pre-line text-slate-200">{e.results}</p> : <None>Official results will be posted after the event.</None>}
          </Block>
          <Block title="Photo Gallery" testid="event-gallery-panel">
            {(e.gallery || []).length ? <Gallery images={e.gallery} testid="event-gallery" /> : <None>Photos coming soon.</None>}
          </Block>
          <Block title="Video Highlights" testid="event-videos">
            {(e.videos || []).length ? <div className="grid gap-5 md:grid-cols-2">{e.videos.map((v, i) => <VideoEmbed key={v + i} url={v} testid={`event-video-${i}`} />)}</div> : <None>Highlights coming soon.</None>}
          </Block>
        </div>
      </section>
    </div>
  );
}
